import { useState } from "react"
import * as Sentry from "@sentry/browser"
import { useAuth } from "../services"

export const localStoragePrefix = "op_"

const debounceMs = 500

let timeout = null

export const useLocalStorage = (key, initialValue, debounce = true) => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key for user
      const item = window.localStorage.getItem(`${localStoragePrefix}_${key}`)
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      Sentry.captureException(error)
      return initialValue
    }
  })
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      // Save state
      setStoredValue(valueToStore)
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(
        () => {
          // Save to local storage
          window.localStorage.setItem(`${localStoragePrefix}_${key}`, JSON.stringify(valueToStore))
        },
        debounce ? debounceMs : 0
      )
    } catch (error) {
      // A more advanced implementation would handle the error case
      Sentry.captureException(error)
    }
  }
  const removeValue = () => {
    try {
      window.localStorage.removeItem(`${localStoragePrefix}_${key}`)
      setStoredValue(null)
    } catch (error) {
      Sentry.captureException(error)
    }
  }
  return [storedValue, setValue, removeValue]
}

export const useUserLocalStorage = (key, initialValue, debounce = true) => {
  const {
    principal: { userID },
  } = useAuth()

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key for user
      const item = window.localStorage.getItem(`${localStoragePrefix}${userID}_${key}`)
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      Sentry.captureException(error)
      return initialValue
    }
  })
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      // Save state
      setStoredValue(valueToStore)
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(
        () => {
          // Save to local storage
          window.localStorage.setItem(`${localStoragePrefix}${userID}_${key}`, JSON.stringify(valueToStore))
        },
        debounce ? debounceMs : 0
      )
    } catch (error) {
      // A more advanced implementation would handle the error case
      Sentry.captureException(error)
    }
  }
  const removeValue = () => {
    try {
      window.localStorage.removeItem(`${localStoragePrefix}${userID}_${key}`)
    } catch (error) {
      Sentry.captureException(error)
    }
  }
  return [storedValue, setValue, removeValue]
}
